import { Badge, Box, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import type { ReactNode } from "react";

export interface RailItem {
  key: string;
  label: string;
  icon: ReactNode;
  /** A count beside the label - jobs waiting, tasks unassigned. */
  count?: number;
  /**
   * How the count reads: a plain figure, work waiting, or something wrong.
   *
   * Three tones rather than one, because "8 diagnoses to record" and "8
   * products with nothing on the shelf" are not the same news. Colour never
   * carries it alone - the number and its label are always there - so a tone
   * nobody can distinguish costs nothing.
   */
  tone?: "neutral" | "waiting" | "alert";
  /**
   * The group this item belongs to.
   *
   * The rail draws a heading and a rule whenever the group changes, so the
   * grouping is expressed once - in the item list - rather than by the caller
   * passing a nested structure the rail would have to flatten anyway.
   *
   * Omitted on Home, which belongs to no group and sits above all of them.
   */
  group?: string;
}

/**
 * The pill's colours per tone, from the design's own values.
 *
 * Plain hex rather than Mantine `color`+`variant`, because these three pairs
 * are specified as exact bg/fg in the brief and a light-variant Badge picks
 * its own shades.
 */
const PILL: Record<
  NonNullable<RailItem["tone"]>,
  { bg: string; fg: string }
> = {
  neutral: { bg: "#f1f3f5", fg: "#495057" },
  waiting: { bg: "#fff9db", fg: "#a1710a" },
  alert: { bg: "#fff5f5", fg: "#c92a2a" },
};

/**
 * The desktop module rail.
 *
 * Like BottomNav, presentational: items in, selection out. Phase 2 feeds it
 * the modules the user holds.
 *
 * Labelled on wide screens and icon-only below lg, which is the brief's rule
 * and also the honest one - an icon alone is a guess unless you already know
 * what it means, so the label only goes when there is genuinely no room.
 */
export function ModuleRail({
  items,
  active,
  onSelect,
  collapsed = false,
  footer,
}: {
  items: RailItem[];
  active?: string;
  onSelect: (key: string) => void;
  collapsed?: boolean;
  /** The user chip, pinned to the bottom. */
  footer?: ReactNode;
}) {
  return (
    <Box
      component="nav"
      aria-label="Modules"
      style={{
        width: collapsed ? 64 : 180,
        flexShrink: 0,
        background: "var(--mantine-color-white)",
        borderRight: "1px solid var(--mantine-color-gray-3)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      <Stack gap={2} p="sm">
        {items.map((item, index) => {
          const isActive = active === item.key;
          // A heading when the group changes. Collapsed rails get the rule
          // only - there is no room for the words, and a truncated group
          // label is worse than none.
          const startsGroup = item.group !== undefined && item.group !== items[index - 1]?.group;
          return (
            <Box key={`${item.key}-wrap`}>
            {startsGroup && (
              <Box
                pt={index === 0 ? 0 : 10}
                pb={4}
                px={collapsed ? 0 : 8}
                style={
                  index === 0
                    ? undefined
                    : { borderTop: "1px solid var(--mantine-color-gray-2)", marginTop: 6 }
                }
              >
                {!collapsed && (
                  <Text
                    fz={11}
                    fw={700}
                    c="gray.6"
                    tt="uppercase"
                    style={{ letterSpacing: ".06em" }}
                  >
                    {item.group}
                  </Text>
                )}
              </Box>
            )}
            <UnstyledButton
              key={item.key}
              onClick={() => onSelect(item.key)}
              aria-current={isActive ? "page" : undefined}
              title={collapsed ? item.label : undefined}
              p="sm"
              style={{
                borderRadius: "var(--mantine-radius-md)",
                background: isActive
                  ? "var(--mantine-color-indigo-0)"
                  : "transparent",
                color: isActive
                  ? "var(--mantine-color-indigo-7)"
                  : "var(--mantine-color-gray-7)",
              }}
            >
              <Group gap="sm" wrap="nowrap" justify={collapsed ? "center" : undefined}>
                {item.icon}
                {!collapsed && (
                  <>
                    <Text fz="md" fw={isActive ? 600 : 400} style={{ color: "inherit", flex: 1 }}>
                      {item.label}
                    </Text>
                    {item.count !== undefined && item.count > 0 && (
                      <Badge
                        size="xs"
                        variant="filled"
                        radius="sm"
                        styles={{
                          root: {
                            background: PILL[item.tone ?? "neutral"].bg,
                            color: PILL[item.tone ?? "neutral"].fg,
                            fontWeight: 700,
                          },
                        }}
                      >
                        {item.count}
                      </Badge>
                    )}
                  </>
                )}
              </Group>
            </UnstyledButton>
            </Box>
          );
        })}
      </Stack>
      {footer && <Box p="sm">{footer}</Box>}
    </Box>
  );
}
